import { useEffect, useRef, useState } from "react";
import { MarkdownWithMath } from "../../components/latex-renderer";
import "./style.sass";
import assets from "../../assets";

export const MediaPopup = ({
  data,
  onClose,
  audioRef,
  videoRef,
  showAskBox,
  setShowAskBox,
  question,
  setQuestion,
  inputRef,
  audioRes,
  videoRes,
  aiAudioChat,
  aiVideoChat,
  isMarkReadVisible,
  markVideo,
  role,
}) => {
  const { content, topic, type, url, filters, display_name } = data || {};

  const [chatList, setChatList] = useState([
    {
      type: "bot",
      text: `Hi! I'm your learning assistant. Ask me anything about ${topic || "this lesson"}!`,
    },
  ]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (showAskBox && inputRef?.current) {
      inputRef.current.focus();
    }
  }, [showAskBox]);

  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN"; // or "ta-IN" for Tamil
    recognition.interimResults = false;

    recognition.onstart = () => setIsListening(true);

    recognition.onend = () => setIsListening(false);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setQuestion((prev) => prev + " " + transcript);
    };

    recognitionRef.current = recognition;
  }, []);

  const handleAsk = async () => {
    if (!question.trim()) return;

    const userMessage = {
      type: "user",
      text: question,
    };

    setChatList((prev) => [...prev, userMessage]);
    setLoading(true);
    setQuestion("");

    try {
      if (type === "audio") {
        await aiAudioChat(filters, content);
      } else {
        await aiVideoChat(filters);
      }
    } catch (e) {
      console.error("Ask failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleMic = () => {
    if (!recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
    }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatList]);

  useEffect(() => {
    if (audioRes?.answer) {
      setChatList((prev) => [...prev, { type: "bot", text: audioRes.answer }]);
    }
  }, [audioRes]);

  useEffect(() => {
    if (videoRes?.answer) {
      setChatList((prev) => [...prev, { type: "bot", text: videoRes.answer }]);
    }
  }, [videoRes]);

  return (
    <section className="audio-overlay">
      <section className="audio-popup">
        <div className="popup-content">
          <button onClick={onClose} className="close-button">
            ✖
          </button>
          {display_name && display_name !== topic && (
            <h4 className="popup-subtitle">{display_name}</h4>
          )}

          <h4 className="topic">{topic}</h4>

          {/* 🎧 AUDIO */}
          {type === "audio" && (
            <audio
              ref={audioRef}
              controls
              className="controls_audio"
              onPause={() => setShowAskBox(true)}
              controlsList="nodownload"
            >
              <source src={url} type="audio/mpeg" />
            </audio>
          )}

          {/* 🎬 VIDEO */}
          {type === "video" && (
            <video
              ref={videoRef}
              controls
              className="video-player"
              controlsList="nodownload"
              width="100%"
              onPause={() => setShowAskBox(true)} // show popup
              onPlay={() => setShowAskBox(false)}
            >
              <source src={url} type="video/mp4" />
            </video>
          )}

          <section className="lesson-content">
            <MarkdownWithMath text={content} />
          </section>

          {showAskBox && (
            <div className="media-ask-popup">
              <div className="ask-header">
                <span>Ask Questions</span>
                <button onClick={() => setShowAskBox(false)}>✖</button>
              </div>

              <div className="ask-body">
                <section className="ask_answer_wrapper">
                  {chatList.map((chat, index) => (
                    <div
                      key={index}
                      className={`chat_message ${
                        chat.type === "user" ? "user_msg" : "bot_msg"
                      }`}
                    >
                      <MarkdownWithMath text={chat.text} />
                    </div>
                  ))}
                  {loading && (
                    <div className="chat_message bot_msg typing">
                      <span>•</span>
                      <span>•</span>
                      <span>•</span>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </section>
                <div className="ask-input">
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Ask about the video..."
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleAsk();
                      }
                    }}
                  />
                  <button
                    className={`mic-btn ${isListening ? "listening" : ""}`}
                    onClick={handleMic}
                  >
                    <img src={assets.ic_mic} alt="mic" />
                    {isListening && <span className="mic-wave"></span>}
                  </button>

                  <button
                    className="send-btn"
                    onClick={handleAsk}
                    disabled={!question.trim() || loading}
                  >
                    ➤
                  </button>
                </div>
              </div>
            </div>
          )}
          {isMarkReadVisible && role !== "guest" && (
            <section className="popup_video_track_container">
              <section className="popup_video_track_wrapper">
                <img
                  src={assets.ic_green_circle_tick}
                  alt="ic_green_circle_tick"
                />
                <section>
                  <h4>Track Your Progress</h4>
                  <p>
                    Mark this lesson as completed to keep your learning streak
                    going.
                  </p>
                </section>
              </section>
              <section
                className="popup_mark_btn"
                onClick={() => markVideo(url)}
              >
                <img src={assets.ic_white_tick} alt="ic_white_tick" />
                <h4>Mark as Compeleted</h4>
              </section>
            </section>
          )}
        </div>
      </section>
    </section>
  );
};
